"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { useLocale } from "next-intl";
import { cn } from "@/lib/utils";
import type { AppLocale } from "@/i18n/routing";

// One line above the plan cards that tells a visitor the page also exists in
// their own language, in that language (approval 346). It used to offer
// «local prices»; English pages now show visitors from DK, SE and NO their own
// currency (src/lib/english-market.ts), so the line is about the language only.
// Failure modes: tests/local-currency-en/failure-modes.md (15–22).

/** The visitor's country (the proxy's gg-country cookie) → the locale of their own page. */
export const COUNTRY_LOCALES: Record<string, AppLocale> = {
  DK: "da",
  DE: "de",
  GR: "el",
  ES: "es",
  FI: "fi",
  FO: "fo",
  FR: "fr",
  IS: "is",
  IT: "it",
  NL: "nl",
  NO: "no",
  PT: "pt",
  SE: "sv"
};

/**
 * Written in the target locale's language, not the page's: the visitor reads
 * their own. One short line each; no prices, currency or promises (approval 300).
 */
export const NOTICE_COPY: Record<string, {
  notice: string;
  dismiss: string;
}> = {
  da: {
    notice: "Siden findes også på dansk",
    dismiss: "Luk forslaget om den danske side"
  },
  de: {
    notice: "Diese Seite gibt es auch auf Deutsch",
    dismiss: "Hinweis auf die deutsche Seite schließen"
  },
  el: {
    notice: "Η σελίδα υπάρχει και στα ελληνικά",
    dismiss: "Κλείσιμο της πρότασης για την ελληνική σελίδα"
  },
  es: {
    notice: "Esta página también está en español",
    dismiss: "Cerrar la sugerencia de la página en español"
  },
  fi: {
    notice: "Tämä sivu on myös suomeksi",
    dismiss: "Sulje ehdotus suomenkielisestä sivusta"
  },
  fo: {
    notice: "Síðan er eisini á føroyskum",
    dismiss: "Lat uppskotið um føroysku síðuna aftur"
  },
  fr: {
    notice: "Cette page existe aussi en français",
    dismiss: "Fermer la suggestion de la page en français"
  },
  is: {
    notice: "Síðan er líka til á íslensku",
    dismiss: "Loka ábendingunni um íslensku síðuna"
  },
  it: {
    notice: "Questa pagina esiste anche in italiano",
    dismiss: "Chiudi il suggerimento della pagina in italiano"
  },
  nl: {
    notice: "Deze pagina is er ook in het Nederlands",
    dismiss: "Suggestie voor de Nederlandse pagina sluiten"
  },
  no: {
    notice: "Siden finnes også på norsk",
    dismiss: "Lukk forslaget om den norske siden"
  },
  pt: {
    notice: "Esta página também existe em português",
    dismiss: "Fechar a sugestão da página em português"
  },
  sv: {
    notice: "Sidan finns även på svenska",
    dismiss: "Stäng förslaget om den svenska sidan"
  }
};

/**
 * The landing count's tag for a tap (`via: "local-notice"`). Not a UTM or a
 * click id: those would replace the visitor's real attribution with this
 * internal hop (src/utils/tracking/attribution.ts).
 */
export const LOCAL_NOTICE_REF = "local-notice";

/**
 * The target's homepage at its prices. A plain link, not next-intl's Link: a
 * locale switch inside the same root layout would be a client navigation,
 * which the landing count (document loads only) never sees.
 */
export function localNoticeHref(targetLocale: AppLocale): string {
  return `/${targetLocale}?gg_ref=${LOCAL_NOTICE_REF}#pricing`;
}
const DISMISS_EVENT = "gg:dismiss-local-pricing-nudge";
const FLAGS: Record<AppLocale, string> = {
  da: "🇩🇰",
  de: "🇩🇪",
  el: "🇬🇷",
  en: "🌍",
  es: "🇪🇸",
  fi: "🇫🇮",
  fo: "🇫🇴",
  fr: "🇫🇷",
  is: "🇮🇸",
  it: "🇮🇹",
  nl: "🇳🇱",
  no: "🇳🇴",
  pt: "🇵🇹",
  sv: "🇸🇪"
};

/** The locale to suggest for this country on this page, or null when there's nothing to suggest. */
export function suggestedLocale(country: string | undefined, pageLocale: string): AppLocale | null {
  const target = country && Object.hasOwn(COUNTRY_LOCALES, country) ? COUNTRY_LOCALES[country] : null;
  return target && target !== pageLocale && NOTICE_COPY[target] ? target : null;
}
export function DetectedLocalPricingNudge(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  const locale = useLocale();
  const [targetLocale, setTargetLocale] = useState(null);
  let t1;
  let t2;
  if ($[0] !== locale) {
    t1 = () => {
      const country = document.cookie.match(/(?:^|; )gg-country=([^;]+)/)?.[1];
      setTargetLocale(suggestedLocale(country, locale));
    };
    t2 = [locale];
    $[0] = locale;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  if (!targetLocale) {
    return null;
  }
  let t3;
  if ($[3] !== className || $[4] !== targetLocale) {
    t3 = <LocalPricingNudge className={className} targetLocale={targetLocale} />;
    $[3] = className;
    $[4] = targetLocale;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
interface LocalPricingNudgeProps {
  className?: string;
  targetLocale: AppLocale;
}
export default function LocalPricingNudge(t0) {
  const $ = _c(25);
  const {
    className,
    targetLocale
  } = t0;
  const [isVisible, setIsVisible] = useState(true);
  const copy = NOTICE_COPY[targetLocale];
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const dismiss = () => setIsVisible(false);
      window.addEventListener(DISMISS_EVENT, dismiss);
      return () => window.removeEventListener(DISMISS_EVENT, dismiss);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  if (!isVisible || !copy) {
    return null;
  }
  let t3;
  if ($[2] !== targetLocale) {
    t3 = () => {
      document.cookie = `NEXT_LOCALE=${targetLocale}; path=/; max-age=31536000; samesite=lax`;
    };
    $[2] = targetLocale;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const persistLocale = t3;
  const dismiss_0 = _temp;
  let t4;
  if ($[4] !== className) {
    t4 = cn("mx-auto flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-2xl border border-[#e7cfaa] bg-[#fffaf2] px-3 py-1.5 text-xs text-[#69583f] shadow-[0_8px_24px_-18px_rgba(83,56,12,0.5)] sm:rounded-full", className);
    $[4] = className;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = FLAGS[targetLocale];
  let t6;
  if ($[6] !== t5) {
    t6 = <span aria-hidden="true">{t5}</span>;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== targetLocale) {
    t7 = localNoticeHref(targetLocale);
    $[8] = targetLocale;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowRight className="h-3 w-3" aria-hidden="true" />;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== copy.notice || $[12] !== persistLocale || $[13] !== t7 || $[14] !== targetLocale) {
    t9 = <a href={t7} hrefLang={targetLocale} onClick={persistLocale} className="inline-flex items-center gap-1 font-semibold text-[#8a570b] underline decoration-[#d9a34e] underline-offset-2 transition-colors hover:text-primary">{copy.notice}{t8}</a>;
    $[11] = copy.notice;
    $[12] = persistLocale;
    $[13] = t7;
    $[14] = targetLocale;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <X className="h-3.5 w-3.5" aria-hidden="true" />;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== copy.dismiss) {
    t11 = <button type="button" onClick={dismiss_0} className="ml-0.5 rounded-full p-0.5 text-[#8c7a60] transition-colors hover:bg-[#f4e7d2] hover:text-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1" aria-label={copy.dismiss}>{t10}</button>;
    $[17] = copy.dismiss;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t4 || $[21] !== t6 || $[22] !== t9 || $[23] !== targetLocale) {
    t12 = <aside lang={targetLocale} className={t4}>{t6}{t9}{t11}</aside>;
    $[19] = t11;
    $[20] = t4;
    $[21] = t6;
    $[22] = t9;
    $[23] = targetLocale;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}
function _temp() {
  window.dispatchEvent(new Event(DISMISS_EVENT));
}